import {
  BoltIcon,
  BookOpenIcon,
  BriefcaseIcon,
  BuildingOffice2Icon,
  DocumentTextIcon,
  IdentificationIcon,
  MagnifyingGlassIcon,
  UsersIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { VariableItem, VariableSection } from './variableSections';

interface VariablePickerProps {
  open: boolean;
  sections: VariableSection[];
  transforms: string[];
  onInsert: (token: string) => void;
  onClose: () => void;
}

const ICONS: Record<string, typeof BoltIcon> = {
  variables: BoltIcon,
  fields: DocumentTextIcon,
  reference: BookOpenIcon,
  People: UsersIcon,
  Company: BuildingOffice2Icon,
  Project: BriefcaseIcon,
  Contact: IdentificationIcon
};

/** A group parent is a row you open, not a row you insert. */
function isGroup(item: VariableItem) {
  return Array.isArray(item.children);
}

function matches(item: VariableItem, query: string): boolean {
  const haystack = `${item.token} ${item.label} ${item.description}`.toLowerCase();
  return haystack.includes(query) || (item.children ?? []).some((child) => matches(child, query));
}

/**
 * TA-12. Replaces the legacy modal in MIGRATE/.../admin/shared.js: the same tab
 * rail, filter, keyboard model and transform menu, but the content is data
 * (see variableSections.ts) and the vocabulary comes from the server.
 */
export function VariablePicker({ open, sections, transforms, onInsert, onClose }: VariablePickerProps) {
  const { t } = useTranslation();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const filterRef = useRef<HTMLInputElement>(null);
  const [tab, setTab] = useState(0);
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState(0);
  const [expanded, setExpanded] = useState<string[]>([]);
  const [chain, setChain] = useState<string[]>([]);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (open && !dialog.open) {
      dialog.showModal();
      // The component stays mounted between openings, so autoFocus would only ever
      // fire once and every later opening would start on the close button.
      filterRef.current?.focus();
    }
    if (!open && dialog.open) dialog.close();
  }, [open]);

  /** Closing resets the view so the next opening does not inherit a stale filter. */
  const dismiss = () => {
    setTab(0);
    setQuery('');
    setSelected(0);
    setExpanded([]);
    setChain([]);
    onClose();
  };

  const needle = query.trim().toLowerCase();

  // Counts stay visible while filtering, so a miss in one tab points at the others.
  const counts = sections.map(
    (section) => section.items.filter((item) => !needle || matches(item, needle)).length
  );

  const activeTab = Math.min(tab, Math.max(sections.length - 1, 0));
  const section = sections[activeTab];

  // Flattened in render order, so arrow keys and the rendered list agree.
  const rows = useMemo(() => {
    if (!section) return [];
    const flat: { item: VariableItem; depth: number }[] = [];
    for (const item of section.items) {
      if (needle && !matches(item, needle)) continue;
      flat.push({ item, depth: 0 });
      if (!isGroup(item)) continue;
      // A filter reveals matching children without requiring a manual expand.
      const isOpen = expanded.includes(item.label) || Boolean(needle);
      if (!isOpen) continue;
      for (const child of item.children ?? []) {
        if (needle && !matches(child, needle)) continue;
        flat.push({ item: child, depth: 1 });
      }
    }
    return flat;
  }, [section, needle, expanded]);

  const cursor = Math.min(selected, Math.max(rows.length - 1, 0));
  const current = rows[cursor]?.item;

  const tokenFor = (item: VariableItem) => {
    if (!item.transformable || chain.length === 0) return item.token;
    const suffix = chain.map((name) => `>${name}`).join('');
    return `{{${item.transformable.name}${suffix}}}`;
  };

  const choose = (item: VariableItem) => {
    if (isGroup(item)) {
      setExpanded((names) =>
        names.includes(item.label) ? names.filter((name) => name !== item.label) : [...names, item.label]
      );
      return;
    }
    onInsert(tokenFor(item));
    dismiss();
  };

  const switchTab = (index: number) => {
    setTab(index);
    setSelected(0);
    setQuery('');
    setChain([]);
  };

  const onKeyDown = (event: React.KeyboardEvent) => {
    // Chrome does not always fire the dialog's own cancel event here, so Escape
    // is handled explicitly rather than left to the platform.
    if (event.key === 'Escape') {
      event.preventDefault();
      dismiss();
      return;
    }

    // Everything below belongs to the filter box; a chip must keep its own Enter.
    if (event.target !== filterRef.current) return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setSelected(Math.min(cursor + 1, rows.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setSelected(Math.max(cursor - 1, 0));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      if (current) choose(current);
    } else if (event.key === 'ArrowRight' && current && isGroup(current)) {
      event.preventDefault();
      choose(current);
    }
  };

  const rowId = (index: number) => `variable-row-${index}`;

  return (
    <dialog
      ref={dialogRef}
      onCancel={(event) => {
        // Escape fires cancel first; without this the reopen effect can win the race.
        event.preventDefault();
        dismiss();
      }}
      onClose={dismiss}
      onKeyDown={onKeyDown}
      onClick={(event) => {
        if (event.target === event.currentTarget) dismiss();
      }}
      aria-label={t('variables.title')}
      className="m-auto w-full max-w-3xl rounded-lg border border-border bg-surface p-0 shadow-xl backdrop:bg-black/50"
    >
      <div className="flex items-center gap-3 border-b border-border px-5 py-4">
        <span className="flex size-9 items-center justify-center rounded-lg bg-primary/12 text-primary">
          <BoltIcon className="size-5" aria-hidden="true" />
        </span>
        <h2 className="flex-1 text-base font-heading font-semibold tracking-normal text-foreground">
          {t('variables.title')}
        </h2>
        <button
          type="button"
          aria-label={t('common.close')}
          onClick={dismiss}
          className="rounded-sm p-1 text-muted-foreground transition hover:bg-muted hover:text-foreground"
        >
          <XMarkIcon className="size-5" aria-hidden="true" />
        </button>
      </div>

      <div className="flex flex-col sm:flex-row">
        <div
          role="tablist"
          aria-orientation="vertical"
          aria-label={t('variables.categories')}
          className="flex shrink-0 gap-1 overflow-x-auto border-b border-border p-2 sm:w-56 sm:flex-col sm:overflow-x-visible sm:border-r sm:border-b-0"
        >
          {sections.map((entry, index) => {
            const Icon = ICONS[entry.kind] ?? DocumentTextIcon;
            const active = index === activeTab;
            return (
              <button
                key={entry.id}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => switchTab(index)}
                className={`flex items-center gap-2 rounded px-3 py-2 text-left text-sm whitespace-nowrap transition focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring ${
                  active ? 'bg-muted font-semibold text-foreground' : 'text-muted-foreground hover:bg-muted/60'
                }`}
              >
                <Icon className="size-4 shrink-0" aria-hidden="true" />
                <span className="flex-1 truncate">{entry.title}</span>
                <span className="rounded-full bg-surface px-2 py-0.5 text-xs text-muted-foreground ring-1 ring-border">
                  {counts[index]}
                </span>
              </button>
            );
          })}
        </div>

        <div className="min-w-0 flex-1 p-4">
          <div className="relative">
            <MagnifyingGlassIcon
              className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground"
              aria-hidden="true"
            />
            <input
              type="text"
              ref={filterRef}
              value={query}
              onChange={(event) => {
                setQuery(event.target.value);
                setSelected(0);
              }}
              placeholder={t('variables.filter')}
              aria-label={t('variables.filter')}
              aria-controls="variable-rows"
              aria-activedescendant={current ? rowId(cursor) : undefined}
              className="w-full rounded border border-input bg-surface py-2 pr-3 pl-9 text-sm outline-none transition focus:border-ring focus:ring-2 focus:ring-ring/22"
            />
          </div>

          <div id="variable-rows" role="listbox" className="mt-3 max-h-96 overflow-y-auto">
            {rows.length === 0 ? (
              <p className="px-2 py-8 text-center text-sm text-muted-foreground">{t('variables.noMatches')}</p>
            ) : (
              rows.map(({ item, depth }, index) => (
                <button
                  key={`${item.label}-${index}`}
                  id={rowId(index)}
                  type="button"
                  role="option"
                  aria-selected={index === cursor}
                  onMouseEnter={() => setSelected(index)}
                  onClick={() => choose(item)}
                  className={`flex w-full items-center gap-3 rounded px-2 py-1.5 text-left transition ${
                    depth > 0 ? 'pl-8' : ''
                  } ${index === cursor ? 'bg-muted' : 'hover:bg-muted/60'}`}
                >
                  <span className="rounded bg-primary/10 px-1.5 py-0.5 font-mono text-xs text-primary">
                    {item.label}
                  </span>
                  <span className="min-w-0 flex-1 truncate text-xs text-muted-foreground italic">
                    {item.description}
                  </span>
                  {item.preview ? (
                    <span className="max-w-[45%] truncate rounded bg-success/10 px-2 py-0.5 text-xs text-success">
                      {item.preview}
                    </span>
                  ) : null}
                  {isGroup(item) ? (
                    <span className="text-xs text-muted-foreground">{item.children?.length}</span>
                  ) : null}
                </button>
              ))
            )}
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 border-t border-border px-5 py-3">
        <span className="text-xs font-semibold text-muted-foreground">{t('variables.insertAs')}</span>
        {transforms.map((name) => {
          const on = chain.includes(name);
          return (
            <button
              key={name}
              type="button"
              aria-pressed={on}
              disabled={!current?.transformable}
              onClick={() =>
                setChain((names) => (names.includes(name) ? names.filter((n) => n !== name) : [...names, name]))
              }
              className={`rounded-full px-2.5 py-1 text-xs transition focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring disabled:cursor-not-allowed disabled:opacity-40 ${
                on ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:text-foreground'
              }`}
            >
              {t(`variables.transforms.${name}`)}
            </button>
          );
        })}
        <span className="ml-auto font-mono text-xs text-muted-foreground">
          {current && !isGroup(current) ? tokenFor(current) : ''}
        </span>
      </div>

      <p className="border-t border-border px-5 py-2 text-center text-xs text-muted-foreground">
        {t('variables.keyboardHint')}
      </p>
    </dialog>
  );
}
